import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { Group } from "@/components/expression/utils/groups/Group";
import { ButtonShortcut } from "@/components/sidebar/Button";
import { useTooltip } from "@/components/tooltip/Tooltip";
import { referenceNeedsBrackets } from "@/models/computedNode/utils";
import { twMerge } from "@/utils/twMerge";

import { ComputedRefIndex, LABEL_CORNERS } from "./ComputedRefIndex";
import { CorneredBox } from "./CorneredBox";
import { InlineNode } from "./InlineNode";
import { useComputedDoc } from "./utils/useComputedDoc";

import type { ExpressionComponent } from "@/components/expression/types";
import type { ComputedNodeManager } from "@/models/computedNode/ComputedNodeManager";
import type { Hotkey } from "@/utils/hotkeys";
import type { ComputedState } from "@corca/data";

export const ComputedReferenceNode: ExpressionComponent<"computedReference"> = observer(
  function ComputedReferenceNode({ node }) {
    const doc = useComputedDoc(node);
    const { computedNodes } = doc;
    const t = useTranslations();

    const targetState = computedNodes.getTargetState(node.targetRefId);
    const targetReason = computedNodes.getTargetInvalidReason(node.targetRefId);
    const refLabel = computedNodes.getComputedRefLabel(node);
    const needsBrackets = referenceNeedsBrackets(node);

    let hotkey: Hotkey | null = null;

    let tooltipTitle: string | undefined;
    if (targetState === "error") {
      tooltipTitle = t("computedNode.tooltip.error");
    } else if (!targetState || targetState === "invalid") {
      if (targetReason === "cycle") tooltipTitle = t("computedNode.tooltip.cycle");
      else if (targetReason === "missing-ref") tooltipTitle = t("computedNode.tooltip.missingRef");
      else if (targetReason === "not-computable")
        tooltipTitle = t("computedNode.tooltip.notComputable");
      else tooltipTitle = t("computedNode.tooltip.invalid");
    } else {
      tooltipTitle = t("computedNode.tooltip.goToSource");
      hotkey = "go-to-source";
    }

    const { handlerProps, tooltip } = useTooltip({
      title: hotkey ? <ButtonShortcut text={tooltipTitle} hotkey={hotkey} /> : tooltipTitle,
      placement: "right",
      hoverProps: {
        delay: {
          open: 1000,
        },
      },
    });
    const classNames = getComputedClassNames(computedNodes, targetState);

    return (
      <>
        <div
          {...handlerProps}
          data-testid="computed-reference"
          data-state={targetState ?? "missing"}
          data-invalid-reason={targetReason}
          className={twMerge("relative", "flex mx-1")}
        >
          <div className="relative flex items-baseline">
            <CorneredBox
              className={twMerge(
                "-inset-x-[4px] -inset-y-[2px]",
                computedNodes.isActiveReference(node.id) ? classNames.active : classNames.default,
              )}
              exclude={refLabel ? LABEL_CORNERS : undefined}
            />
            {node.inlineNode &&
              (needsBrackets ? (
                <Group
                  node={node}
                  parameters={[node.inlineNode]}
                  openingSymbol="("
                  closingSymbol=")"
                />
              ) : (
                <InlineNode node={node.inlineNode} className="" />
              ))}
          </div>
          {refLabel && <ComputedRefIndex label={refLabel} />}
        </div>
        {tooltip}
      </>
    );
  },
);

function getComputedClassNames(
  computedNodes: ComputedNodeManager,
  targetState: ComputedState | undefined,
) {
  if (!computedNodes.ready) {
    return {
      default: "text-gray-500/50",
      active: "text-gray-500/50 bg-gray-50 dark:bg-gray-950",
    };
  }
  if (targetState !== "valid") {
    return {
      default: "text-red-500",
      active: "text-red-500 bg-red-50 dark:bg-red-950",
    };
  }
  return {
    default: "text-green-500",
    active: "text-green-500 bg-green-50 dark:bg-green-950",
  };
}
